'use client'

export const dynamic = 'force-dynamic'

import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { useFeatureFlags } from '../../hooks/useFeatureFlags'
import {
  ArrowRight,
  Bell,
  Bookmark,
  Building2,
  CheckCircle2,
  Heart,
  LockKeyhole,
  MapPin,
  MessageSquare,
  ReceiptText,
  Users,
  Settings,
  ShieldCheck,
  Sparkles,
  UserRound,
  Copy,
  Check,
  Share2,
  Gift,
} from 'lucide-react'
import { OnboardingNudge } from '../../components/onboarding'
import {
  AIRecommendation,
  Badge,
  Button,
  ButtonLink,
  Card,
  LoadingSurface,
  MetricPill,
  StatusBadge,
} from '../../components/ui'
import { useSocial } from '../../hooks/useSocial'

type AccountTab = 'overview' | 'masjids' | 'giving' | 'saved' | 'connect' | 'notifications' | 'security' | 'roles'

type AccountUser = {
  id?: string
  name?: string
  email?: string
  phone?: string
  role?: string
  referralCode?: string | null
  referralCount?: number
}

type Donation = {
  id?: string
  amount?: string | number
  donatedAt?: string
  createdAt?: string
  campaignId?: string
  campaign?: { titleEnglish?: string }
}

const tabs: Array<{ key: AccountTab; label: string; icon: LucideIcon }> = [
  { key: 'overview', label: 'Overview', icon: UserRound },
  { key: 'masjids', label: 'Followed masjids', icon: Building2 },
  { key: 'giving', label: 'Donations', icon: Heart },
  { key: 'saved', label: 'Saved', icon: Bookmark },
  { key: 'connect', label: 'Masjid Connect', icon: Users },
  { key: 'notifications', label: 'Notifications', icon: Bell },
  { key: 'security', label: 'Privacy', icon: LockKeyhole },
  { key: 'roles', label: 'Roles', icon: ShieldCheck },
]

export default function AccountPage() {
  const { follows, saved, unreadCount } = useSocial()
  const { isFlagEnabled } = useFeatureFlags()
  const searchParams = useSearchParams()
  const router = useRouter()
  // Connect tab shows ONLY when masjidConnect is on (default off → hidden). The flag hook
  // starts from FEATURE_CONTROL_DEFAULTS (masjidConnect false) then confirms from the server,
  // so the tab never flashes on before the flag is known.
  const connectOn = isFlagEnabled('masjidConnect')
  const visibleTabs = useMemo(
    () => (connectOn ? tabs : tabs.filter((t) => t.key !== 'connect')),
    [connectOn],
  )
  const tabParam = searchParams.get('tab') as AccountTab | null
  const [tab, setTab] = useState<AccountTab>(tabParam && tabs.some((item) => item.key === tabParam) ? tabParam : 'overview')
  const [user, setUser] = useState<AccountUser | null>(null)
  const [donations, setDonations] = useState<Donation[]>([])
  const [masjidsSubmitted, setMasjidsSubmitted] = useState<number | null>(null)
  const [topSubmitters, setTopSubmitters] = useState<{ name: string; count: number }[]>([])
  const [loading, setLoading] = useState(true)
  const [copiedRef, setCopiedRef] = useState(false)
  const [notifPrefs, setNotifPrefs] = useState({ janaza: true, announcements: true, campaigns: true, prayers: false, offers: false, jobs: false, waqf: true })
  // Offer-skills detection for the "Volunteer your profession" nudge: true = has an active
  // talent_profile, false = none (show the nudge), null = unknown/not checked. Checked ONLY when
  // masjidConnect is on — the whole feature is dark otherwise, so the nudge never shows.
  const [hasOffer, setHasOffer] = useState<boolean | null>(null)
  useEffect(() => {
    if (!connectOn) { setHasOffer(null); return }
    let alive = true
    fetch('/api/connect/profile')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) setHasOffer(!!(d?.profile && d.profile.isActive !== false)) })
      .catch(() => { if (alive) setHasOffer(null) })
    return () => { alive = false }
  }, [connectOn])

  useEffect(() => {
    if (tabParam && tabs.some((item) => item.key === tabParam)) setTab(tabParam)
  }, [tabParam])

  // If ?tab=connect lands while the flag is off, fall back to overview — never show the
  // Connect body without the flag, even via a deep link.
  useEffect(() => {
    if (tab === 'connect' && !connectOn) setTab('overview')
  }, [tab, connectOn])

  useEffect(() => {
    async function loadUser() {
      try {
        const res = await fetch('/api/auth/me')
        const data = await res.json()
        if (data.user) setUser(data.user)
        else router.push('/login?redirect=/account')
      } catch {
        router.push('/login?redirect=/account')
      } finally {
        setLoading(false)
      }
    }
    loadUser()
  }, [router])

  useEffect(() => {
    if (!user?.id) return
    fetch(`/api/donations?userId=${user.id}`)
      .then((res) => res.json())
      .then((data) => setDonations(data.donations || []))
      .catch(() => setDonations([]))
  }, [user?.id])

  // Submitter attribution — the signed-in user's REAL submitted count (/api/me/stats) + the public
  // Top Submitters ranking (/api/leaderboard/submitters). Both are real/honest-empty: most users
  // have 0, and only ~7 masjids across ~4 users are attributable today (the rest were imported).
  useEffect(() => {
    if (!user?.id) return
    fetch('/api/me/stats').then((r) => (r.ok ? r.json() : null)).then((d) => { if (d) setMasjidsSubmitted(Number(d.masjidsSubmitted) || 0) }).catch(() => {})
    fetch('/api/leaderboard/submitters').then((r) => (r.ok ? r.json() : null)).then((d) => { if (d?.submitters) setTopSubmitters(d.submitters) }).catch(() => {})
  }, [user?.id])

  const totalDonated = useMemo(() => donations.reduce((sum, donation) => sum + Number(donation.amount || 0), 0), [donations])
  const displayName = user?.name || 'Community member'
  const displayInitial = displayName.charAt(0).toUpperCase()
  const civicLevel = follows.length + donations.length + saved.length > 8 ? 'Trusted contributor' : 'Civic starter'

  // Referrals v1 — the invite card's data + actions. Real code + honest count; no fabricated proof.
  const referralLink = user?.referralCode ? `https://masjids.in/?ref=${user.referralCode}` : ''
  const copyReferral = () => {
    if (!user?.referralCode) return
    try { navigator.clipboard.writeText(user.referralCode); setCopiedRef(true); setTimeout(() => setCopiedRef(false), 1600) } catch { /* clipboard blocked */ }
  }
  const shareReferral = async () => {
    if (!user?.referralCode) return
    const text = `Every masjid you help someone find is a sadaqah jariyah — join me on Masjids.in with my code ${user.referralCode}`
    try {
      if (typeof navigator !== 'undefined' && (navigator as any).share) await (navigator as any).share({ title: 'Masjids.in', text, url: referralLink })
      else { await navigator.clipboard.writeText(`${text} ${referralLink}`); setCopiedRef(true); setTimeout(() => setCopiedRef(false), 1600) }
    } catch { /* user cancelled */ }
  }

  function changeTab(next: AccountTab) {
    setTab(next)
    router.replace(`/account?tab=${next}`, { scroll: false })
  }

  function toggleNotif(key: keyof typeof notifPrefs) {
    setNotifPrefs((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  if (loading) {
    return (
      <main className="entry-page">
        <div className="entry-shell-narrow">
          <LoadingSurface rows={5} />
        </div>
      </main>
    )
  }

  return (
    <main className="entry-page">
      <div className="entry-shell">
        <section className="entry-card">
          <Card tone="intelligence" padding="lg" className="entry-account-hero">
            <div className="entry-list-main">
              <span className="entry-avatar">{displayInitial}</span>
              <div>
                <Badge tone="gold">{civicLevel}</Badge>
                <h1 className="entry-title entry-title-light">{displayName}</h1>
                <p className="entry-lede entry-lede-light">
                  {user?.email || user?.phone || 'Your personal Muslim civic profile'} {user?.role ? `- ${user.role}` : ''}
                </p>
                <div className="entry-actions">
                  <ButtonLink href="/directory" size="sm" variant="secondary" rightIcon={<ArrowRight size={14} />}>Find masjids</ButtonLink>
                  <ButtonLink href="/campaigns" size="sm" variant="gold">Support campaigns</ButtonLink>
                </div>
              </div>
            </div>
            <div className="entry-signal-grid">
              <div className="entry-signal"><strong>{follows.length}</strong><span>followed masjids</span></div>
              <div className="entry-signal"><strong>{donations.length}</strong><span>donations</span></div>
              <div className="entry-signal"><strong>{unreadCount}</strong><span>unread alerts</span></div>
            </div>
          </Card>

          <div className="entry-tabs-scroll" role="tablist" aria-label="Account sections">
            {visibleTabs.map((item) => {
              const Icon = item.icon
              return (
                <button key={item.key} type="button" className={tab === item.key ? 'is-active' : ''} onClick={() => changeTab(item.key)}>
                  <Icon size={15} /> {item.label}
                </button>
              )
            })}
          </div>
        </section>

        <div className="entry-layout">
          <section className="entry-card">
            {tab === 'overview' && (
              <>
                {/* ── Referral invite card (prominent) — real code + honest count, no fabricated proof ── */}
                {user?.referralCode ? (
                  <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 18, border: '1px solid rgba(212,175,55,.38)', background: 'linear-gradient(150deg, #FDF7E3, #fff)', padding: '22px 22px 20px', marginBottom: 18, boxShadow: '0 2px 8px rgba(11,19,43,.05), 0 14px 34px -14px rgba(11,19,43,.16)' }}>
                    <span aria-hidden style={{ position: 'absolute', top: -30, right: -20, width: 120, height: 120, borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,175,55,.16), transparent 70%)' }} />
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: 'var(--f-mono)', fontSize: 10.5, letterSpacing: '.16em', textTransform: 'uppercase', color: '#B8932B', fontWeight: 700 }}>
                      <Gift size={13} strokeWidth={2} /> Invite friends
                    </div>
                    <p style={{ margin: '10px 0 18px', fontFamily: 'var(--font-display)', fontSize: 'clamp(17px, 2vw, 21px)', lineHeight: 1.35, color: 'var(--f-ink)', fontWeight: 600, maxWidth: 540 }}>
                      Every masjid you help someone find is a sadaqah jariyah — share Masjids.in
                    </p>
                    <button type="button" onClick={copyReferral} aria-label="Copy your referral code" style={{ display: 'inline-flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px dashed rgba(184,147,43,.6)', borderRadius: 12, padding: '12px 16px', cursor: 'pointer', marginBottom: 16 }}>
                      <span style={{ fontFamily: 'var(--f-mono)', fontSize: 24, fontWeight: 700, letterSpacing: '.14em', color: 'var(--f-ink)' }}>{user.referralCode}</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 700, color: copiedRef ? '#0E7C61' : '#B8932B' }}>
                        {copiedRef ? <><Check size={15} strokeWidth={2.4} /> Copied!</> : <><Copy size={15} strokeWidth={2} /> Tap to copy</>}
                      </span>
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                      <Button onClick={shareReferral} variant="gold" leftIcon={<Share2 size={16} />}>Share your invite</Button>
                      <span style={{ fontSize: 13.5, color: user.referralCount && user.referralCount > 0 ? 'var(--f-ink)' : 'var(--f-slate)', fontWeight: user.referralCount && user.referralCount > 0 ? 600 : 400 }}>
                        {user.referralCount && user.referralCount > 0
                          ? `${user.referralCount} ${user.referralCount === 1 ? 'person has' : 'people have'} joined through you`
                          : 'Be the first to invite someone'}
                      </span>
                    </div>
                  </div>
                ) : null}

                {/* "Volunteer your profession" nudge — ONLY for masjidConnect-on users who have no
                    active offer yet (hasOffer === false). No count, no fake urgency; dismissed simply
                    by offering (the card disappears once a profile exists). */}
                {connectOn && hasOffer === false && (
                  <Card padding="lg">
                    <div className="entry-card-head">
                      <div>
                        <span className="entry-kicker"><Users size={14} /> Masjid Connect</span>
                        <h2>Volunteer your profession at your masjid</h2>
                        <p>Offer your skills to your masjid community — you choose who sees you.</p>
                      </div>
                      <ButtonLink href="/connect/profile" size="sm" rightIcon={<ArrowRight size={14} />}>Offer my skills</ButtonLink>
                    </div>
                  </Card>
                )}
                <div className="entry-card-grid">
                  <MetricPill label="Giving impact" value={`Rs ${Math.round(totalDonated).toLocaleString('en-IN')}`} tone="emerald" />
                  <MetricPill label="Masjids submitted" value={masjidsSubmitted == null ? '—' : String(masjidsSubmitted)} tone="info" />
                  <MetricPill label="Saved items" value={String(saved.length)} tone="gold" />
                  <MetricPill label="Notifications" value={String(unreadCount)} tone="info" />
                </div>
                <AIRecommendation
                  title="Recommended next civic action"
                  body={follows.length ? 'You are following masjids. Save one local campaign or enable prayer alerts to make your account more useful.' : 'Follow your nearest masjid first so announcements, funeral prayer notices, classes, and campaigns become personalized.'}
                  action={<ButtonLink href={follows.length ? '/campaigns' : '/directory'} size="sm" rightIcon={<ArrowRight size={14} />}>{follows.length ? 'Browse campaigns' : 'Find masjid'}</ButtonLink>}
                />
                <Card padding="lg">
                  <div className="entry-card-head">
                    <div>
                      <span className="entry-kicker"><Sparkles size={14} /> Civic identity</span>
                      <h2>Your ecosystem profile</h2>
                    </div>
                    <StatusBadge tone="emerald">Active</StatusBadge>
                  </div>
                  <div className="entry-card-grid">
                    {[
                      ['Community member', 'Follow local institutions and receive trusted updates.'],
                      ['Supporter', donations.length ? 'Donation history is connected to your account.' : 'Your first donation will appear here.'],
                      ['Saved ecosystem', saved.length ? `${saved.length} saved items are ready.` : 'Save campaigns, posts, and businesses for later.'],
                    ].map(([title, body]) => (
                      <Card key={title} tone="muted" padding="md">
                        <h3 className="card-title">{title}</h3>
                        <p className="entry-muted">{body}</p>
                      </Card>
                    ))}
                  </div>
                </Card>

                {/* Top Masjid Submitters — REAL public ranking (names only, no email). Honest-empty
                    when nobody has submitted; today it's a short real list (top ~3), never padded. */}
                {topSubmitters.length > 0 && (
                  <Card padding="lg">
                    <div className="entry-card-head">
                      <div>
                        <span className="entry-kicker"><Sparkles size={14} /> Community</span>
                        <h2>Top Masjid Submitters</h2>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gap: 8 }}>
                      {topSubmitters.map((s, i) => (
                        <div key={`${s.name}-${i}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#f8f8f6', borderRadius: 10 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <b style={{ color: '#0E7C61', minWidth: 18 }}>{i + 1}</b>
                            <span style={{ color: '#0B132B', fontWeight: 600 }}>{s.name}</span>
                          </span>
                          <span style={{ color: '#475569', fontSize: 13 }}>{s.count} {s.count === 1 ? 'masjid' : 'masjids'}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </>
            )}

            {tab === 'masjids' && (
              <Card padding="lg" className="entry-card">
                <div className="entry-card-head">
                  <div>
                    <span className="entry-kicker"><Building2 size={14} /> Followed masjids</span>
                    <h2>Institutions you care about</h2>
                  </div>
                  <ButtonLink href="/directory" size="sm" variant="secondary">Find more</ButtonLink>
                </div>
                {follows.length === 0 ? (
                  <EmptyState icon={<Building2 size={34} />} title="No followed masjids yet" body="Follow a masjid to receive prayer updates, funeral prayer notices, classes, campaigns, and community announcements." href="/directory" action="Find masjids" />
                ) : (
                  <div className="entry-list">
                    {follows.map((item) => (
                      <Link key={item.id} href={`/masjids/${item.slug || item.id}`} className="entry-list-item">
                        <span className="entry-list-main">
                          <span className="entry-icon-box"><Building2 size={18} /></span>
                          <span>
                            <strong>{item.name}</strong>
                            <p className="entry-muted">{item.area || item.city || 'Local masjid'} {item.verified ? '- verified' : ''}</p>
                          </span>
                        </span>
                        <ArrowRight size={16} />
                      </Link>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {tab === 'giving' && (
              <Card padding="lg" className="entry-card">
                <div className="entry-card-head">
                  <div>
                    <span className="entry-kicker"><Heart size={14} /> Donations</span>
                    <h2>Your giving history</h2>
                  </div>
                  <ButtonLink href="/campaigns" size="sm" variant="gold">Give again</ButtonLink>
                </div>
                {donations.length === 0 ? (
                  <EmptyState icon={<Heart size={34} />} title="No donations yet" body="Support a verified campaign when you are ready. Receipts and impact updates will appear here." href="/campaigns" action="Browse campaigns" />
                ) : (
                  <div className="entry-list">
                    {donations.map((donation, index) => (
                      <div key={donation.id || index} className="entry-list-item">
                        <span className="entry-list-main">
                          <span className="entry-icon-box"><ReceiptText size={18} /></span>
                          <span>
                            <strong>{donation.campaign?.titleEnglish || donation.campaignId || 'Campaign donation'}</strong>
                            <p className="entry-muted">{new Date(donation.donatedAt || donation.createdAt || Date.now()).toLocaleDateString('en-IN')}</p>
                          </span>
                        </span>
                        <StatusBadge tone="emerald">Rs {Number(donation.amount || 0).toLocaleString('en-IN')}</StatusBadge>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {tab === 'saved' && (
              <Card padding="lg" className="entry-card">
                <div className="entry-card-head">
                  <div>
                    <span className="entry-kicker"><Bookmark size={14} /> Saved ecosystem</span>
                    <h2>Saved campaigns, posts, and places</h2>
                  </div>
                </div>
                {saved.length === 0 ? (
                  <EmptyState icon={<Bookmark size={34} />} title="Nothing saved yet" body="Use save actions across campaigns, masjids, businesses, and posts to build your personal civic library." href="/search" action="Explore ecosystem" />
                ) : (
                  <div className="entry-list">
                    {saved.map((item) => (
                      <div key={item} className="entry-list-item">
                        <span className="entry-list-main">
                          <span className="entry-icon-box"><Bookmark size={18} /></span>
                          <span>
                            <strong>{item}</strong>
                            <p className="entry-muted">Saved item</p>
                          </span>
                        </span>
                        <StatusBadge tone="gold">Saved</StatusBadge>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {tab === 'connect' && connectOn && (
              <Card padding="lg" className="entry-card">
                <div className="entry-card-head">
                  <div>
                    <span className="entry-kicker"><Users size={14} /> Masjid Connect</span>
                    <h2>Offer your help</h2>
                  </div>
                </div>
                <p className="entry-muted" style={{ marginBottom: 18, lineHeight: 1.6 }}>
                  List how you can serve your community — your profession and skills — so a neighbour
                  who prays beside you can find the doctor, teacher or tradesman they need. Your contact
                  details stay private and are revealed only after <strong>you</strong> approve a request.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  <Link
                    href="/connect/profile"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#0E7C61', color: '#fff', borderRadius: 10, padding: '11px 20px', fontWeight: 700, fontSize: 14, textDecoration: 'none' }}
                  >
                    <Users size={16} /> Set up my Connect profile <ArrowRight size={15} />
                  </Link>
                  <Link
                    href="/connect/requests"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fff', color: '#0B132B', border: '1.5px solid #e6e4dc', borderRadius: 10, padding: '11px 20px', fontWeight: 700, fontSize: 14, textDecoration: 'none' }}
                  >
                    Connection requests <ArrowRight size={15} />
                  </Link>
                </div>
              </Card>
            )}

            {tab === 'notifications' && (
              <Card padding="lg" className="entry-card">
                <div className="entry-card-head">
                  <div>
                    <span className="entry-kicker"><Bell size={14} /> Preferences</span>
                    <h2>Notification controls</h2>
                  </div>
                  <ButtonLink href="/notifications" size="sm" variant="secondary">Open inbox</ButtonLink>
                </div>
                {Object.entries(notifPrefs).map(([key, enabled]) => (
                  <div key={key} className="entry-toggle-row">
                    <div>
                      <strong>{key.charAt(0).toUpperCase() + key.slice(1)} alerts</strong>
                      <p className="entry-muted">Receive trusted civic updates for this category.</p>
                    </div>
                    <button className={`entry-switch ${enabled ? 'is-on' : ''}`} type="button" onClick={() => toggleNotif(key as keyof typeof notifPrefs)} aria-pressed={enabled}>
                      <span />
                    </button>
                  </div>
                ))}
              </Card>
            )}

            {tab === 'security' && (
              <Card padding="lg" className="entry-card">
                <div className="entry-card-head">
                  <div>
                    <span className="entry-kicker"><LockKeyhole size={14} /> Privacy and security</span>
                    <h2>Account safety</h2>
                  </div>
                  <StatusBadge tone="info">Basic</StatusBadge>
                </div>
                <div className="entry-card-grid">
                  {[
                    ['Profile visibility', 'Your public activity can be expanded into privacy controls later.'],
                    ['Session security', 'Use a strong password and avoid sharing committee credentials.'],
                    ['Data controls', 'Donation receipts, saved content, and role access should remain account-bound.'],
                  ].map(([title, body]) => (
                    <Card key={title} tone="muted" padding="md">
                      <h3 className="card-title">{title}</h3>
                      <p className="entry-muted">{body}</p>
                    </Card>
                  ))}
                </div>
                <div className="entry-note">
                  <ShieldCheck size={16} />
                  <span>Security settings are intentionally conservative here. Backend auth behavior was not changed.</span>
                </div>
              </Card>
            )}

            {tab === 'roles' && (
              <Card padding="lg" className="entry-card">
                <div className="entry-card-head">
                  <div>
                    <span className="entry-kicker"><ShieldCheck size={14} /> Linked roles</span>
                    <h2>Role and dashboard access</h2>
                  </div>
                  <StatusBadge tone={user?.role ? 'emerald' : 'info'}>{user?.role || 'member'}</StatusBadge>
                </div>
                <div className="entry-card-grid">
                  {[
                    ['Personal member', 'Default role for following, saving, donations, and notifications.', '/dashboard'],
                    ['Committee access', 'Claim or register a masjid before committee tools are activated.', '/masjid/claim'],
                    ['Volunteer access', 'Join field missions and verification workflows.', '/volunteer/dashboard'],
                  ].map(([title, body, href]) => (
                    <Card key={title} tone="muted" padding="md">
                      <h3 className="card-title">{title}</h3>
                      <p className="entry-muted">{body}</p>
                      <ButtonLink href={href} size="sm" variant="outline" rightIcon={<ArrowRight size={14} />}>Open</ButtonLink>
                    </Card>
                  ))}
                </div>
              </Card>
            )}
          </section>

          <aside className="entry-help-rail">
            <OnboardingNudge
              eyebrow="Account guidance"
              title="Your account becomes useful after one civic action."
              body="Follow a masjid, support a campaign, or save something important. Those actions shape your alerts, recommendations, and impact history."
              steps={[
                { title: 'Follow. ', body: 'Start with your nearest masjid.' },
                { title: 'Support. ', body: 'Save or donate to a trusted cause.' },
                { title: 'Personalize. ', body: 'Tune notification preferences.' },
              ]}
              actions={[
                { href: '/directory', label: 'Find masjid', variant: 'primary' },
                { href: '/campaigns', label: 'Browse campaigns', variant: 'secondary' },
              ]}
            />
            <Card padding="md">
              <div className="entry-list">
                <div className="entry-list-main">
                  <span className="entry-icon-box"><MapPin size={17} /></span>
                  <div><strong>Preferred locality</strong><p className="entry-muted">Add city/location later to improve nearby recommendations.</p></div>
                </div>
                <div className="entry-list-main">
                  <span className="entry-icon-box"><MessageSquare size={17} /></span>
                  <div><strong>Community updates</strong><p className="entry-muted">{unreadCount} unread notifications available.</p></div>
                </div>
                <div className="entry-list-main">
                  <span className="entry-icon-box"><Settings size={17} /></span>
                  <div><strong>Preferences</strong><p className="entry-muted">Notification controls are available in this profile center.</p></div>
                </div>
              </div>
            </Card>
          </aside>
        </div>
      </div>
    </main>
  )
}

function EmptyState({ icon, title, body, href, action }: { icon: ReactNode; title: string; body: string; href: string; action: string }) {
  return (
    <div className="entry-empty">
      {icon}
      <h3>{title}</h3>
      <p className="entry-muted">{body}</p>
      <ButtonLink href={href} rightIcon={<ArrowRight size={14} />}>{action}</ButtonLink>
    </div>
  )
}
